import React, { useMemo } from 'react';
import { CircularProgress, MenuItem, TextField } from '@material-ui/core';
import clsx from 'clsx';
import useStyles from 'app/cluster-styles/useStyles';
import useProviderData from '../hooks/useProviderData';

/**
 * Region dropdown.
 *
 * By default the list comes from the provider catalog (providers.json). A
 * caller that knows the regions live — the AWS form asks the account — passes
 * `options` and the catalog is not consulted at all. `loading` shows a spinner
 * row; `disabled` + `disabledLabel` cover "pick a credential first".
 */
export default function RegionSelector({
    provider, value, onChange, allowedRegionIds, className,
    options, loading = false, disabled = false, disabledLabel, error = false, helperText,
}) {
    const classes = useStyles();
    const { regions: catalogRegions } = useProviderData(provider);
    const regions = options || catalogRegions;

    const visibleRegions = useMemo(() => {
        if (!allowedRegionIds || allowedRegionIds.length === 0) return regions;
        return regions.filter((r) => allowedRegionIds.includes(r.region_id));
    }, [regions, allowedRegionIds]);

    const safeValue = regions.some((r) => r.region_id === value) ? value : '';
    const isDisabled = disabled || loading;

    return (
        <TextField
            select
            className={clsx(classes.input, className || "flex-1")}
            id="rm-region"
            name="rm_region_id"
            label={disabled && disabledLabel ? disabledLabel : 'Region'}
            variant="outlined"
            value={safeValue}
            onChange={(e) => onChange(e.target.value)}
            required={!disabled}
            disabled={isDisabled}
            error={error}
            helperText={helperText}
            SelectProps={{
                MenuProps: {
                    PaperProps: {
                        style: { maxHeight: 300 },
                    },
                },
            }}
        >
            {loading && (
                <MenuItem disabled>
                    <CircularProgress size={16} className="mr-8" />
                    Loading regions...
                </MenuItem>
            )}
            {visibleRegions.map((region) => (
                <MenuItem key={region.region_id} value={region.region_id}>
                    {region.name}
                </MenuItem>
            ))}
        </TextField>
    );
}
